import React, { useState } from 'react';
import theme from '../../../theme/globalTheme';
import { Upload, Button, Card, List, Typography, Space, Alert, Progress, Tag, Collapse, Input, Modal, Divider } from 'antd';
import { UploadOutlined, DownloadOutlined, FileImageOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, FilePdfOutlined, SendOutlined, MailOutlined } from '@ant-design/icons';
import heic2any from 'heic2any';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import JSZip from 'jszip';

const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Panel } = Collapse;

const PassportCompressor = () => {
  const [compressedFiles, setCompressedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [emailGendarmerie, setEmailGendarmerie] = useState('');
  const [emailSecondary, setEmailSecondary] = useState('');
  const [sendResults, setSendResults] = useState(null);

  const WEBHOOK_URL = 'https://hook.eu2.make.com/xtarun1odi8hypgtb7vmse8kgcyeq9uo';
  const MAX_SIZE_GENDARMERIE = 20 * 1024 * 1024; // 20 MB en bytes

  const compressImage = async (file, originalFileName) => {
    try {
      const img = document.createElement('img');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const loadImage = new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
      });

      await loadImage;

      const MAX_WIDTH = 1080;
      const MAX_HEIGHT = 1080;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              blob.originalName = originalFileName;
              resolve(blob);
            } else {
              reject(new Error('Failed to create compressed image blob'));
            }
          },
          'image/jpeg',
          0.7
        );
      });
    } catch (err) {
      throw new Error(`Error compressing image: ${err.message}`);
    }
  };

  const convertPdfToImages = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const compressedImages = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2.0 });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const blob = await new Promise((resolve) => {
          canvas.toBlob((blob) => {
            blob.originalName = `${file.name}_page_${pageNum}`;
            resolve(blob);
          }, 'image/jpeg', 0.7);
        });

        const compressedBlob = await compressImage(blob, `${file.name}_page_${pageNum}`);
        compressedImages.push(compressedBlob);
      }

      return compressedImages;
    } catch (err) {
      throw new Error(`Error converting PDF: ${err.message}`);
    }
  };

  const handleFileUpload = async (info) => {
    const { fileList } = info;
    setUploading(true);
    setErrors([]);
    setSuccessCount(0);
    setFailureCount(0);

    const compressedResults = [];
    const errorsList = [];
    let totalFiles = fileList.length;
    let processedFiles = 0;
    let successfulFiles = 0;
    let failedFiles = 0;

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i].originFileObj;
      const fileName = file.name;

      try {
        let results = [];

        if (file.type === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf')) {
          const pdfImages = await convertPdfToImages(file);
          results = pdfImages;
        }
        else if (file.type === 'image/heic' || fileName.toLowerCase().endsWith('.heic')) {
          const blob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8
          });
          const convertedFile = new File([blob], fileName.replace(/\.heic$/i, '.jpg'), {
            type: 'image/jpeg'
          });
          const compressedFile = await compressImage(convertedFile, fileName);
          results = [compressedFile];
        }
        else if (file.type.startsWith('image/')) {
          const compressedFile = await compressImage(file, fileName);
          results = [compressedFile];
        }
        else {
          throw new Error(`Format non supporté: ${file.type || 'inconnu'}`);
        }

        compressedResults.push(...results);
        successfulFiles++;
        processedFiles++;
        setProgress((processedFiles / totalFiles) * 100);

      } catch (err) {
        console.error('Error processing file:', fileName, err);
        errorsList.push({
          fileName: fileName,
          error: err.message
        });
        failedFiles++;
        processedFiles++;
        setProgress((processedFiles / totalFiles) * 100);
      }
    }

    setCompressedFiles([...compressedFiles, ...compressedResults]);
    setErrors(errorsList);
    setSuccessCount(successfulFiles);
    setFailureCount(failedFiles);
    setUploading(false);
    setProgress(0);
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const chunkFilesForGendarmerie = (files) => {
    const chunks = [];
    let currentChunk = [];
    let currentSize = 0;

    for (const file of files) {
      if (currentSize + file.size > MAX_SIZE_GENDARMERIE && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentSize = 0;
      }
      currentChunk.push(file);
      currentSize += file.size;
    }

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  };

  const sendToWebhook = async () => {
    if (!emailGendarmerie || !emailSecondary) {
      Modal.error({
        title: 'Adresses email manquantes',
        content: 'Veuillez renseigner les deux adresses email avant d\'envoyer.',
      });
      return;
    }

    if (compressedFiles.length === 0) {
      Modal.error({
        title: 'Aucun fichier',
        content: 'Veuillez compresser des fichiers avant d\'envoyer.',
      });
      return;
    }

    setSending(true);
    setSendProgress(0);
    setSendResults(null);

    const tomorrowDate = getTomorrowDate();
    const messageText = `Veuillez bien vouloir trouver les passeports pour la date du ${tomorrowDate}.\n\nBonne soirée,\nMaster Ski Pilou`;

    try {
      const results = {
        gendarmerie: { success: false, chunks: 0, errors: [] },
        secondary: { success: false, errors: [] }
      };

      // Préparer les chunks pour la gendarmerie
      const gendarmerieChunks = chunkFilesForGendarmerie(compressedFiles);
      results.gendarmerie.chunks = gendarmerieChunks.length;

      // Envoyer à la gendarmerie (avec chunks)
      for (let chunkIndex = 0; chunkIndex < gendarmerieChunks.length; chunkIndex++) {
        const chunk = gendarmerieChunks[chunkIndex];
        
        try {
          const filesData = await Promise.all(
            chunk.map(async (file, index) => ({
              filename: file.originalName || `passport_${index + 1}.jpg`,
              content: await blobToBase64(file),
              size: file.size
            }))
          );

          const chunkMessage = gendarmerieChunks.length > 1 
            ? `${messageText}\n\n(Partie ${chunkIndex + 1}/${gendarmerieChunks.length})`
            : messageText;

          const payload = {
            recipient: emailGendarmerie,
            recipientType: 'gendarmerie',
            subject: `Passeports - ${tomorrowDate}${gendarmerieChunks.length > 1 ? ` (${chunkIndex + 1}/${gendarmerieChunks.length})` : ''}`,
            message: chunkMessage,
            files: filesData,
            chunkInfo: {
              current: chunkIndex + 1,
              total: gendarmerieChunks.length
            }
          };

          const response = await fetch(WEBHOOK_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          setSendProgress(((chunkIndex + 1) / (gendarmerieChunks.length + 1)) * 100);
        } catch (error) {
          results.gendarmerie.errors.push(`Chunk ${chunkIndex + 1}: ${error.message}`);
        }
      }

      if (results.gendarmerie.errors.length === 0) {
        results.gendarmerie.success = true;
      }

      // Envoyer à l'email secondaire (tous les fichiers en une fois)
      try {
        const filesData = await Promise.all(
          compressedFiles.map(async (file, index) => ({
            filename: file.originalName || `passport_${index + 1}.jpg`,
            content: await blobToBase64(file),
            size: file.size
          }))
        );

        const payload = {
          recipient: emailSecondary,
          recipientType: 'secondary',
          subject: `Passeports - ${tomorrowDate}`,
          message: messageText,
          files: filesData
        };

        const response = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        results.secondary.success = true;
      } catch (error) {
        results.secondary.errors.push(error.message);
      }

      setSendProgress(100);
      setSendResults(results);

      // Afficher le modal de confirmation
      if (results.gendarmerie.success && results.secondary.success) {
        Modal.success({
          title: '✅ Envoi réussi !',
          content: (
            <div>
              <p><strong>Tous les passeports ont été envoyés avec succès !</strong></p>
              <Divider />
              <p>📧 <strong>Gendarmerie</strong> ({emailGendarmerie}):</p>
              <p style={{ marginLeft: '20px' }}>
                ✓ {compressedFiles.length} fichier(s) envoyé(s) en {gendarmerieChunks.length} email(s)
              </p>
              <p>📧 <strong>Email secondaire</strong> ({emailSecondary}):</p>
              <p style={{ marginLeft: '20px' }}>
                ✓ {compressedFiles.length} fichier(s) envoyé(s) en 1 email
              </p>
              <Divider />
              <p>📅 Date : {tomorrowDate}</p>
            </div>
          ),
          width: 600,
        });
      } else {
        Modal.warning({
          title: '⚠️ Envoi partiel',
          content: (
            <div>
              <p><strong>Certains envois ont échoué :</strong></p>
              <Divider />
              {!results.gendarmerie.success && (
                <>
                  <p>❌ <strong>Gendarmerie</strong> ({emailGendarmerie}):</p>
                  <ul>
                    {results.gendarmerie.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </>
              )}
              {results.gendarmerie.success && (
                <p>✓ <strong>Gendarmerie</strong>: {compressedFiles.length} fichier(s) envoyé(s)</p>
              )}
              {!results.secondary.success && (
                <>
                  <p>❌ <strong>Email secondaire</strong> ({emailSecondary}):</p>
                  <ul>
                    {results.secondary.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </>
              )}
              {results.secondary.success && (
                <p>✓ <strong>Email secondaire</strong>: {compressedFiles.length} fichier(s) envoyé(s)</p>
              )}
            </div>
          ),
          width: 600,
        });
      }

    } catch (error) {
      console.error('Error sending files:', error);
      Modal.error({
        title: '❌ Erreur d\'envoi',
        content: `Une erreur est survenue lors de l'envoi: ${error.message}`,
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadAll = async () => {
    const zip = new JSZip();
    const date = new Date().toISOString().split('T')[0];

    compressedFiles.forEach((file, index) => {
      const originalName = file.originalName || `compressed_file_${index + 1}`;
      const cleanName = originalName.replace(/\.[^/.]+$/, '');
      const fileName = `${cleanName}_compressed.jpg`;
      zip.file(fileName, file);
    });

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(zipBlob);
    link.download = `passeports_${date}.zip`;
    link.click();
  };

  const handleDeleteAll = () => {
    setCompressedFiles([]);
    setErrors([]);
    setSuccessCount(0);
    setFailureCount(0);
    setSendResults(null);
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    accept: 'image/*,.heic,.pdf',
    fileList: [],
    customRequest: ({ onSuccess }) => {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    onChange: handleFileUpload,
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }}>
    <Card title={<Title level={3}>Compresseur de Passeports</Title>}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Email Configuration */}
        <Card title="Configuration des emails" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Email Gendarmerie (max 20 Mo par email) :</Text>
              <Input
                placeholder="email@gendarmerie.fr"
                value={emailGendarmerie}
                onChange={(e) => setEmailGendarmerie(e.target.value)}
                prefix={<MailOutlined />}
                style={{ marginTop: '8px' }}
              />
            </div>
            <div>
              <Text strong>Email secondaire (Gmail/Hotmail) :</Text>
              <Input
                placeholder="email@gmail.com ou email@hotmail.com"
                value={emailSecondary}
                onChange={(e) => setEmailSecondary(e.target.value)}
                prefix={<MailOutlined />}
                style={{ marginTop: '8px' }}
              />
            </div>
          </Space>
        </Card>

        {/* Success/Failure Summary */}
        {(successCount > 0 || failureCount > 0) && (
          <Alert
            message={
              <Space>
                <Text>Compression terminée :</Text>
                {successCount > 0 && (
                  <Tag icon={<CheckCircleOutlined />} color="success">
                    {successCount} fichier{successCount > 1 ? 's' : ''} compressé{successCount > 1 ? 's' : ''}
                  </Tag>
                )}
                {failureCount > 0 && (
                  <Tag icon={<CloseCircleOutlined />} color="error">
                    {failureCount} échec{failureCount > 1 ? 's' : ''}
                  </Tag>
                )}
              </Space>
            }
            type={failureCount > 0 ? "warning" : "success"}
            showIcon
          />
        )}

        {/* Error Details */}
        {errors.length > 0 && (
          <Collapse>
            <Panel
              header={
                <Text type="danger">
                  <CloseCircleOutlined /> {errors.length} fichier{errors.length > 1 ? 's' : ''} en erreur - Cliquez pour voir les détails
                </Text>
              }
              key="1"
            >
              <List
                size="small"
                dataSource={errors}
                renderItem={(error) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<CloseCircleOutlined style={{ color: theme.colors.textSecondary }} />}
                      title={<Text strong>{error.fileName}</Text>}
                      description={<Text type="danger">{error.error}</Text>}
                    />
                  </List.Item>
                )}
              />
            </Panel>
          </Collapse>
        )}

        <Dragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Cliquez ou glissez les fichiers ici
          </p>
          <p className="ant-upload-hint">
            <strong>Formats supportés :</strong> JPEG, JPG, PNG, HEIC, WebP, BMP, GIF, PDF
          </p>
          <p className="ant-upload-hint" style={{ fontSize: '12px', marginTop: '8px' }}>
            📄 Les PDFs seront convertis en images (une image par page)
          </p>
        </Dragger>

        {uploading && (
          <Progress 
            percent={Math.round(progress)} 
            status="active" 
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
        )}

        {sending && (
          <Progress 
            percent={Math.round(sendProgress)} 
            status="active"
            strokeColor={{
              '0%': '#ff4d4f',
              '100%': '#52c41a',
            }}
            format={percent => `Envoi en cours... ${percent}%`}
          />
        )}

        {compressedFiles.length > 0 && (
          <Card 
            title={`Fichiers compressés (${compressedFiles.length})`}
            extra={
              <Space>
                <Button 
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={sendToWebhook}
                  loading={sending}
                  disabled={!emailGendarmerie || !emailSecondary}
                  size="large"
                  style={{ backgroundColor: theme.colors.lightGrey, borderColor: '#52c41a' }}
                >
                  Envoyer par Email
                </Button>
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadAll}
                  disabled={sending}
                >
                  Télécharger Tout
                </Button>
                <Button 
                  type="primary" 
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteAll}
                  disabled={sending}
                >
                  Supprimer Tout
                </Button>
              </Space>
            }
          >
            <List
              dataSource={compressedFiles}
              renderItem={(file, index) => {
                const originalName = file.originalName || `compressed_file_${index + 1}`;
                const cleanName = originalName.replace(/\.[^/.]+$/, '');
                const isPdfPage = originalName.includes('_page_');

                return (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        href={URL.createObjectURL(file)}
                        download={`${cleanName}_compressed_${new Date().toISOString().split('T')[0]}.jpg`}
                        icon={<DownloadOutlined />}
                      >
                        Télécharger
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={isPdfPage ? <FilePdfOutlined style={{ fontSize: '20px', color: theme.colors.textSecondary }} /> : <FileImageOutlined style={{ fontSize: '20px', color: theme.colors.white }} />}
                      title={
                        <Space>
                          <Text>{originalName}</Text>
                          {isPdfPage && <Tag color="red">Page PDF</Tag>}
                        </Space>
                      }
                      description={`Taille : ${(file.size / 1024).toFixed(2)} KB`}
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        )}
      </Space>
    </Card>
    </div>
  );
};

export default PassportCompressor;